/**
 * Finance API Hooks
 * React hooks for fetching finance data from BFF
 * Falls back to dummy data when API is unavailable (development mode)
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchApi, FINANCE_API, CONSUMABLES_API } from '@/api/client'
import {
  generateAccountUsageData,
  generateUsageSummary,
} from '@/features/finance/data/dummyData'
import type {
  AccountUsageResponse,
  AccountUsageData,
  DataFlowResponse,
  AccountYtdTotals,
  UsageSummary,
  LicenseInfo,
  ConsumablesReportResponse,
  ConsumablesViewType,
} from '@/api/types'
import type { AccountUsageData as DummyAccountUsageData } from '@/features/finance/types'

interface UseApiResult<T> {
  data: T | null
  loading: boolean
  error: Error | null
  refetch: () => void
}

// Enable dummy data fallback (set to true for development/demo)
const USE_DUMMY_DATA_FALLBACK = false

/**
 * Transform dummy data to API response format
 */
function transformDummyDataToApiFormat(
  dummyAccounts: DummyAccountUsageData[]
): AccountUsageResponse {
  const accounts: AccountUsageData[] = dummyAccounts.map((acc) => ({
    accId: acc.accId,
    accountName: acc.accountName,
    license: {
      tier: acc.license.tier.name,
      yearlyLimit: acc.license.yearlyLimit,
      monthlyAverage: acc.license.monthlyAverage,
      pricePerEvent: acc.license.tier.pricePerYear / acc.license.yearlyLimit,
      overageRate: acc.license.tier.overageRate,
    } as LicenseInfo,
    yearToDateUsage: acc.yearToDateUsage,
    utilizationPercent: acc.utilizationPercent,
    projectedYearlyUsage: acc.projectedYearlyUsage,
    projectedOverage: acc.projectedOverage,
    status: acc.status,
    monthlyUsage: acc.monthlyUsage,
  }))

  const dummySummary = generateUsageSummary(dummyAccounts)

  const summary: UsageSummary = {
    totalAccounts: dummySummary.totalAccounts,
    totalYtdEvents: dummySummary.totalYtdUsage,
    totalLicensedEvents: dummySummary.totalYearlyLicensedEvents,
    overallUtilization: Math.round(
      (dummySummary.totalYtdUsage / dummySummary.totalYearlyLicensedEvents) * 100
    ),
    healthyCount: accounts.filter((a) => a.status === 'healthy').length,
    warningCount: accounts.filter((a) => a.status === 'warning').length,
    criticalCount: accounts.filter((a) => a.status === 'critical').length,
    underutilizedCount: accounts.filter((a) => a.status === 'underutilized').length,
    projectedOverageRevenue: dummySummary.potentialOverageRevenue,
    churnRiskAmount: dummySummary.potentialChurnRisk,
  }

  return {
    accounts,
    summary,
    lastUpdated: new Date().toISOString(),
  }
}

/**
 * Hook to fetch account usage data for all accounts
 */
export function useAccountUsage(year?: number): UseApiResult<AccountUsageResponse> {
  const [data, setData] = useState<AccountUsageResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = year ? `?year=${year}` : ''
      const response = await fetchApi<AccountUsageResponse>(`${FINANCE_API}/usage${params}`)

      // Check if response has valid data (BFF may return 200 with empty/null data on upstream failure)
      const hasValidData = response?.accounts && response.accounts.length > 0

      if (hasValidData) {
        setData(response)
      } else if (USE_DUMMY_DATA_FALLBACK) {
        // Fallback to dummy data when API returns empty response
        console.info('[Finance API] Using dummy data (API returned empty response)')
        const dummyAccounts = generateAccountUsageData()
        const dummyResponse = transformDummyDataToApiFormat(dummyAccounts)
        setData(dummyResponse)
      } else {
        setData(response)
      }
    } catch (err) {
      // Fallback to dummy data in development
      if (USE_DUMMY_DATA_FALLBACK) {
        console.info('[Finance API] Using dummy data (API unavailable)')
        const dummyAccounts = generateAccountUsageData()
        const response = transformDummyDataToApiFormat(dummyAccounts)
        setData(response)
      } else {
        setError(err instanceof Error ? err : new Error('Failed to fetch account usage'))
      }
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Transform a single dummy account to API format
 */
function transformSingleAccountToApiFormat(
  dummyAccount: DummyAccountUsageData
): AccountUsageData {
  return {
    accId: dummyAccount.accId,
    accountName: dummyAccount.accountName,
    license: {
      tier: dummyAccount.license.tier.name,
      yearlyLimit: dummyAccount.license.yearlyLimit,
      monthlyAverage: dummyAccount.license.monthlyAverage,
      pricePerEvent:
        dummyAccount.license.tier.pricePerYear / dummyAccount.license.yearlyLimit,
      overageRate: dummyAccount.license.tier.overageRate,
    } as LicenseInfo,
    yearToDateUsage: dummyAccount.yearToDateUsage,
    utilizationPercent: dummyAccount.utilizationPercent,
    projectedYearlyUsage: dummyAccount.projectedYearlyUsage,
    projectedOverage: dummyAccount.projectedOverage,
    status: dummyAccount.status,
    monthlyUsage: dummyAccount.monthlyUsage,
  }
}

/**
 * Hook to fetch usage data for a specific account
 */
export function useAccountUsageById(
  accountId: number,
  year?: number
): UseApiResult<AccountUsageData> {
  const [data, setData] = useState<AccountUsageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    if (!accountId) return

    setLoading(true)
    setError(null)
    try {
      const params = year ? `?year=${year}` : ''
      const response = await fetchApi<AccountUsageData>(
        `${FINANCE_API}/usage/account/${accountId}${params}`
      )

      // Check if response has valid data (BFF may return 200 with empty/null data on upstream failure)
      const hasValidData = response?.accId && response?.accountName

      if (hasValidData) {
        setData(response)
      } else if (USE_DUMMY_DATA_FALLBACK) {
        // Fallback to dummy data when API returns empty response
        console.info('[Finance API] Using dummy data for account', accountId, '(API returned empty response)')
        const dummyAccounts = generateAccountUsageData()
        const account = dummyAccounts.find((a) => a.accId === accountId)
        if (account) {
          setData(transformSingleAccountToApiFormat(account))
        } else {
          setError(new Error(`Account ${accountId} not found`))
        }
      } else {
        setData(response)
      }
    } catch (err) {
      // Fallback to dummy data in development
      if (USE_DUMMY_DATA_FALLBACK) {
        console.info('[Finance API] Using dummy data for account', accountId)
        const dummyAccounts = generateAccountUsageData()
        const account = dummyAccounts.find((a) => a.accId === accountId)
        if (account) {
          setData(transformSingleAccountToApiFormat(account))
        } else {
          setError(new Error(`Account ${accountId} not found`))
        }
      } else {
        setError(err instanceof Error ? err : new Error('Failed to fetch account usage'))
      }
    } finally {
      setLoading(false)
    }
  }, [accountId, year])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook to fetch data flow report
 */
export function useDataFlow(
  startDate?: string,
  endDate?: string
): UseApiResult<DataFlowResponse> {
  const [data, setData] = useState<DataFlowResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      const queryString = params.toString()
      const url = `${FINANCE_API}/dataflow${queryString ? `?${queryString}` : ''}`
      const response = await fetchApi<DataFlowResponse>(url)
      setData(response)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch data flow'))
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook to fetch data flow for a specific account
 */
export function useAccountDataFlow(
  accountId: number,
  startDate?: string,
  endDate?: string
): UseApiResult<DataFlowResponse> {
  const [data, setData] = useState<DataFlowResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    if (!accountId) return

    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      const queryString = params.toString()
      const url = `${FINANCE_API}/dataflow/account/${accountId}${queryString ? `?${queryString}` : ''}`
      const response = await fetchApi<DataFlowResponse>(url)
      setData(response)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch account data flow'))
    } finally {
      setLoading(false)
    }
  }, [accountId, startDate, endDate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook to fetch YTD totals for an account
 */
export function useAccountYtdTotals(
  accountId: number,
  year?: number
): UseApiResult<AccountYtdTotals> {
  const [data, setData] = useState<AccountYtdTotals | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    if (!accountId) return

    setLoading(true)
    setError(null)
    try {
      const params = year ? `?year=${year}` : ''
      const response = await fetchApi<AccountYtdTotals>(
        `${FINANCE_API}/dataflow/account/${accountId}/ytd${params}`
      )
      setData(response)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch YTD totals'))
    } finally {
      setLoading(false)
    }
  }, [accountId, year])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook to fetch the consumables outbound event report (date / month / YTD)
 */
export function useConsumablesReport(params: {
  viewType: ConsumablesViewType
  channel?: string
  startDate?: string
  endDate?: string
  year?: number
}): UseApiResult<ConsumablesReportResponse> {
  const [data, setData] = useState<ConsumablesReportResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const q = new URLSearchParams({ viewType: params.viewType })
      if (params.channel && params.channel !== 'ALL') q.set('channel', params.channel)
      if (params.startDate) q.set('startDate', params.startDate)
      if (params.endDate) q.set('endDate', params.endDate)
      if (params.year) q.set('year', String(params.year))
      const response = await fetchApi<ConsumablesReportResponse>(
        `${CONSUMABLES_API}/report?${q.toString()}`
      )
      setData(response)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch consumables report'))
    } finally {
      setLoading(false)
    }
  }, [params.viewType, params.channel, params.startDate, params.endDate, params.year])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook to fetch license summary
 */
export function useLicenseSummary(year?: number): UseApiResult<UsageSummary> {
  const [data, setData] = useState<UsageSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = year ? `?year=${year}` : ''
      const response = await fetchApi<UsageSummary>(`${FINANCE_API}/licenses/summary${params}`)
      setData(response)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch license summary'))
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}
